<template>
  <div v-if="isHeadCounterOrManager">
    <b-button variant="primary" size="sm" :disabled="isBusy" @click="$refs.importModal.show()">
      <b-spinner v-if="isBusy" small class="mr-1" />
      <i v-else class="fas fa-file-import mr-1"></i>
      {{ isBusy ? copy.busy : copy.button }}
    </b-button>

    <b-modal ref="importModal" :title="copy.title" hide-footer>
      <p class="mb-2">{{ copy.what }}</p>
      <ol class="import-steps small text-muted">
        <li v-for="(step, i) in copy.steps" :key="i">{{ step }}</li>
      </ol>
      <b-form-file
        ref="fileInput"
        v-model="file"
        :accept="copy.accept"
        placeholder="ลากไฟล์มาวางที่นี่ หรือกดเลือกไฟล์"
        browse-text="เลือกไฟล์"
        drop-placeholder="วางไฟล์ที่นี่..."
        :disabled="isBusy"
        @input="onFileSelected"
      />
      <small class="text-muted d-block mt-1">รองรับไฟล์ {{ copy.types }}</small>
    </b-modal>

    <OcrReviewModal ref="ocrReviewModal" :payer="payer" @committed="onImported" />
    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import OcrReviewModal from "@/components/reconciliation/OcrReviewModal.vue";
import Dialog from "@/components/modal/Dialog.vue";
import { apiErrorMessage } from "@/utils/reconciliation";

// What each payer's import is for, in the words of the documents staff actually receive.
const IMPORT_COPY = {
  SSO: {
    button: "นำเข้าใบคำสั่งจ่าย ปกส.",
    busy: "กำลังอ่านเอกสาร...",
    title: "นำเข้าใบคำสั่งจ่ายจากประกันสังคม",
    what:
      "ใช้เมื่อได้รับเงินจากสำนักงานประกันสังคมพร้อมเอกสาร “คำสั่งประโยชน์ทดแทนและใบคำสั่งจ่าย” ระบบจะอ่านเลขบัตร ปชช. วันที่ใช้สิทธิ และจำนวนเงินจากเอกสาร แล้วจับคู่กับรายการค้างรับให้",
    steps: [
      "สแกนหรือถ่ายรูปเอกสารให้เห็นตารางครบทุกแถว (PDF หลายหน้าได้)",
      "ตรวจและแก้ข้อมูลที่ระบบอ่านได้ก่อนกดบันทึก",
      "รายการที่จับคู่ได้ย้ายไป “รับแล้ว” ที่เหลืออยู่ใน “รอตรวจสอบ”",
    ],
    accept: "application/pdf,image/jpeg,image/png",
    types: "PDF, JPG หรือ PNG",
    failed: "อ่านข้อมูลจากเอกสารไม่สำเร็จ กรุณาลองใหม่",
  },
  NHSO: {
    button: "นำเข้าไฟล์ REP สปสช.",
    busy: "กำลังนำเข้า...",
    title: "นำเข้าไฟล์ REP จาก สปสช.",
    what:
      "ใช้เมื่อ สปสช. โอนเงินชดเชยแล้ว ดาวน์โหลดไฟล์ REP ของงวดนั้นจากระบบ e-Claim แล้วนำเข้าที่นี่ ระบบจะจับคู่ visit ที่ได้รับชดเชยด้วยเลขบัตร ปชช. และวันที่ใช้สิทธิให้",
    steps: [
      "ดาวน์โหลดไฟล์ REP (Excel) จาก e-Claim สปสช.",
      "เลือกไฟล์ด้านล่าง ระบบนำเข้าทันที (เฉพาะรายการที่ “ชดเชย”)",
      "visit ที่จับคู่ได้ย้ายไป “รับแล้ว” ที่เหลืออยู่ใน “รอตรวจสอบ”",
    ],
    accept: ".xlsx,.xls,.csv",
    types: "Excel (.xlsx)",
    failed: "นำเข้าไฟล์ไม่สำเร็จ กรุณาลองใหม่",
  },
};

export default {
  name: "ImportControls",
  components: { OcrReviewModal, Dialog },
  props: {
    payer: { type: String, required: true },
  },
  data() {
    return {
      file: null,
      isBusy: false,
    };
  },
  computed: {
    ...mapGetters({ isAccessMenu: "moduleUser/getIsAccessMenu" }),
    // reconciliation.manage (upload/OCR import) is MANAGER/HEADCOUNTER only on
    // the backend; COUNTER only has reconciliation.view. Gate both upload
    // controls here so a COUNTER sees a read-only page instead of an input
    // whose submit 403s.
    isHeadCounterOrManager() {
      return this.isAccessMenu(["MANAGER", "HEADCOUNTER"]);
    },
    copy() {
      return IMPORT_COPY[this.payer];
    },
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
  },
  methods: {
    ...mapActions("moduleReconciliation", ["ocrExtract", "uploadStatement"]),
    async onFileSelected(file) {
      if (!file) return;
      this.$refs.importModal.hide();
      this.isBusy = true;
      try {
        if (this.payer === "SSO") await this.readSsoStatement(file);
        else await this.importNhsoFile(file);
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, this.copy.failed));
      } finally {
        this.isBusy = false;
        this.file = null;
        if (this.$refs.fileInput) this.$refs.fileInput.reset();
      }
    },
    async readSsoStatement(file) {
      const formData = new FormData();
      formData.append("file", file);
      const res = await this.ocrExtract({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, formData });
      // Nothing is persisted by ocr-extract — hand the extracted rows to the review modal for staff
      // correction; commitReviewed is the only write.
      this.$refs.ocrReviewModal.open(res.data, file);
    },
    async importNhsoFile(file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("payer", this.payer);
      // statementRef/depositAmount/statementDate are optional here — the REP workbook itself carries
      // them and the backend falls back to the file's own values when they're omitted.
      const res = await this.uploadStatement({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, formData });
      this.$emit("imported", res.data);
    },
    onImported(result) {
      this.$emit("imported", result);
    },
  },
};
</script>

<style scoped>
.import-steps {
  padding-left: 1.2rem;
  margin-bottom: 12px;
}
</style>
